import { Box, HStack, VStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import BigTokens from 'components/BigTokens';
import Icon from 'components/Icon';
import { LinkInput } from 'components/LinkInput';
import Modal from 'components/Modal';
import ShareButtons from 'components/ShareButtons';
import Text from 'components/Text';
import { IModal } from 'types/frontend';
import { CreditReward } from 'utils/constants';
import { referralBuilder } from 'utils/strings';

export const REFERRAL_SHARE_TEXT =
  'Join me on BigDevSoon — practice real-world projects and level up your dev skills!';
export const REFERRAL_EMAIL_SUBJECT = 'Join me on BigDevSoon';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface IInviteModalProps extends IModal {}

export const InviteModal = ({ isOpen, onClose }: IInviteModalProps) => {
  const { referralCode, hasExhaustedReferralLink } = useCurrentUser();

  const referralLink = referralBuilder(referralCode);

  return (
    <Modal size="xl" isOpen={isOpen} onClose={onClose} header={null} footer={null}>
      <VStack spacing={5} py={4} px={1}>
        {/* Header with icon */}
        <VStack spacing={3} animation={`${fadeIn} 0.5s ease-out`}>
          <Box
            p={3}
            borderRadius="full"
            bg="whiteAlpha.100"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <Icon type="InviteFriendsModal" size={28} />
          </Box>
          <VStack spacing={1}>
            <Text fontSize="xLarge" fontWeight="bold">
              Invite friends
            </Text>
            {hasExhaustedReferralLink ? (
              <Text fontWeight="regular" opacity="secondary" textAlign="center" fontSize="small">
                {`You've used all 5 invites! Share your link anyway.`}
              </Text>
            ) : (
              <HStack spacing={1} flexWrap="wrap" justifyContent="center">
                <Text fontWeight="regular" opacity="secondary" fontSize="small">
                  Earn
                </Text>
                <BigTokens amount={CreditReward.Referral} />
                <Text fontSize="small">BigToken</Text>
                <Text fontWeight="regular" opacity="secondary" fontSize="small">
                  per signup
                </Text>
                <Text fontWeight="regular" opacity="tertiary" fontSize="micro">
                  (up to 5)
                </Text>
              </HStack>
            )}
          </VStack>
        </VStack>

        {/* Referral link card */}
        <Box
          w="100%"
          p={4}
          borderRadius={12}
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
          animation={`${fadeIn} 0.5s ease-out 0.1s both`}
        >
          <VStack spacing={3}>
            <Text fontSize="small" opacity="secondary" fontWeight="semibold">
              Your referral link
            </Text>
            <LinkInput />
          </VStack>
        </Box>

        {/* Share section */}
        <VStack
          spacing={3}
          w="100%"
          animation={`${fadeIn} 0.5s ease-out 0.2s both`}
        >
          <Text fontSize="small" opacity="secondary" fontWeight="semibold">
            Share via
          </Text>
          <ShareButtons
            text={REFERRAL_SHARE_TEXT}
            link={referralLink}
            emailSubject={REFERRAL_EMAIL_SUBJECT}
          />
        </VStack>
      </VStack>
    </Modal>
  );
};
